import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'

export default function EditBoxModal({ isOpen, onClose, box, onSave }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: box?.name || '',
      description: box?.description || '',
      contribution_amount: box?.contribution_amount || '',
      cycle_duration_months: box?.cycle_duration_months || 6,
      payouts_per_month: box?.payouts_per_month || 1,
      start_date: box?.start_date || '',
    },
  })

  // Reset form when box changes
  useEffect(() => {
    if (box) {
      reset({
        name: box.name || '',
        description: box.description || '',
        contribution_amount: box.contribution_amount || '',
        cycle_duration_months: box.cycle_duration_months || 6,
        payouts_per_month: box.payouts_per_month || 1,
        start_date: box.start_date || '',
      })
    }
  }, [box, reset])

  const onSubmit = async (data) => {
    try {
      setSaving(true)
      setError(null)
      const payload = {
        ...data,
        contribution_amount: parseFloat(data.contribution_amount),
        cycle_duration_months: parseInt(data.cycle_duration_months, 10),
        payouts_per_month: parseInt(data.payouts_per_month, 10),
      }
      await onSave(payload)
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to update box')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-3xl bg-white p-5 sm:p-6 text-left align-middle shadow-xl transition-all max-h-[82vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold text-gray-900"
                  >
                    Edit Box Details
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-full p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-base font-semibold text-gray-900 mb-2"
                    >
                      Box Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', { required: 'Box name is required' })}
                      className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Enter box name"
                    />
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-base font-semibold text-gray-900 mb-2"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      rows={4}
                      {...register('description')}
                      className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                      placeholder="Describe your rotating savings box (optional)"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="contribution_amount"
                        className="block text-base font-semibold text-gray-900 mb-2"
                      >
                        Contribution Amount ($) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-base font-medium">$</span>
                        </div>
                        <input
                          type="number"
                          id="contribution_amount"
                          step="0.01"
                          {...register('contribution_amount', {
                            required: 'Contribution amount is required',
                            min: { value: 0.01, message: 'Must be greater than 0' },
                          })}
                          className="block w-full pl-8 pr-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="0.00"
                        />
                      </div>
                      {errors.contribution_amount && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.contribution_amount.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="cycle_duration_months"
                        className="block text-base font-semibold text-gray-900 mb-2"
                      >
                        Cycle Duration (months) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id="cycle_duration_months"
                        {...register('cycle_duration_months', {
                          required: 'Cycle duration is required',
                          min: { value: 1, message: 'Must be at least 1 month' },
                        })}
                        className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="6"
                      />
                      {errors.cycle_duration_months && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.cycle_duration_months.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="payouts_per_month"
                        className="block text-base font-semibold text-gray-900 mb-2"
                      >
                        Payouts per Month <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id="payouts_per_month"
                        {...register('payouts_per_month', {
                          required: 'Payouts per month is required',
                          min: { value: 1, message: 'Must be at least 1' },
                        })}
                        className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="1"
                      />
                      {errors.payouts_per_month && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.payouts_per_month.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="start_date"
                      className="block text-base font-semibold text-gray-900 mb-2"
                    >
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      {...register('start_date')}
                      className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>

                  <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-3 border border-gray-200 rounded-full shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-3 border border-transparent rounded-full shadow-sm text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
